import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import './FinancesPage.css';

interface Transaction {
  id: string;
  amount: number;
  transaction_type: string;
  description: string;
  league_id: string;
  team_name: string;
  created_at: string;
}

const FinancesPage: React.FC<{ userData?: any }> = ({ userData }) => {
  const [balance, setBalance] = React.useState<number>(0);
  const [transactions, setTransactions] = React.useState<Transaction[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (isSupabaseConfigured() && supabase && userData?.id) {
      // Load balance
      supabase!.from('users')
        .select('balls_balance')
        .eq('id', userData.id)
        .single()
        .then(({ data }) => {
          if (data) setBalance(data.balls_balance || 0);
        });

      // Load transactions
      supabase!.from('balls_transactions')
        .select('*')
        .eq('user_id', userData.id)
        .order('created_at', { ascending: false })
        .limit(50)
        .then(({ data }) => {
          if (data) setTransactions(data as Transaction[]);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [userData]);

  const totalEarned = transactions.filter(t => t.amount > 0).reduce((s, t) => s + t.amount, 0);
  const totalSpent = transactions.filter(t => t.amount < 0).reduce((s, t) => s + Math.abs(t.amount), 0);

  return (
    <div className="finances-page">
      <h1>💰 Finances</h1>
      <p className="subtitle">Your $BALLS balance and transaction history.</p>

      <div className="finance-summary">
        <div className="finance-card balance-card">
          <span className="finance-value">{balance.toLocaleString()}</span>
          <span className="finance-label">$BALLS Balance</span>
        </div>
        <div className="finance-card">
          <span className="finance-value earned">+{totalEarned.toLocaleString()}</span>
          <span className="finance-label">Total Earned</span>
        </div>
        <div className="finance-card">
          <span className="finance-value spent">-{totalSpent.toLocaleString()}</span>
          <span className="finance-label">Total Spent</span>
        </div>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : !userData?.id ? (
        <div className="sign-in-prompt">
          <p>Sign in to view your finances.</p>
        </div>
      ) : transactions.length === 0 ? (
        <div className="no-transactions">
          <p>No transactions yet. Join a quick league to get started!</p>
        </div>
      ) : (
        <div className="transactions-wrapper">
          <h3>Transaction History</h3>
          <table className="transactions-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Description</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(t => (
                <tr key={t.id}>
                  <td className="tx-date">{new Date(t.created_at).toLocaleDateString()}</td>
                  <td className="tx-type">{t.transaction_type === 'entry_fee' ? '🎟️ Entry Fee' : t.transaction_type === 'prize' ? '🏆 Prize' : t.transaction_type}</td>
                  <td className="tx-desc">{t.description || t.team_name || '—'}</td>
                  <td className={t.amount > 0 ? 'tx-positive' : 'tx-negative'}>
                    {t.amount > 0 ? '+' : ''}{t.amount.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FinancesPage;
